import React, { useState } from 'react';
import { Thead, Tbody } from '@chakra-ui/react';

import type {
  AvailableCarrier,
  CarrierQuote,
  CarriersLocation,
  CarriersInsuranceHistory,
  RiskDetailsSync,
} from 'api/quote-marketplace';

import { useComparisonTableHelpers, useExpandableItems } from '../../hooks';
import { formatCurrency } from '../../constants';
import { ComparisonTableHeader } from './ComparisonTableHeader';
import type { CarrierSortOption } from '../../hooks';
import { LocationCoveragesSection } from './LocationCoveragesSection';
import { MarketMessagesSection } from './MarketMessagesSection';
import {
  TableWrapper,
  ItemHeaderRow,
  PremiumRow,
  ItemSeparatorRow,
  MonthlyPremiumSection,
} from './SharedTableComponents';

interface PropertyCarriersComparisonTableProps {
  availableCarriers: AvailableCarrier[];
  carrierQuotes: CarrierQuote[];
  locations: CarriersLocation[];
  insuranceHistory: CarriersInsuranceHistory;
  selectedCarrierQuoteId: number | null;
  rfqStatus: string;
  onSelectCarrier: (carrierQuoteId: number) => void;
  onEditCoverages?: (locationId?: number) => void;
  onOpenMarketOptions: () => void;
  onGetQuotes: () => void;
  isSelectingCarrier?: boolean;
  isGettingQuotes?: boolean;
  canGetQuotes?: boolean;
  isCapturingRiskDetails?: boolean;
  riskDetailsSync?: RiskDetailsSync;
  sortBy?: CarrierSortOption;
  onSortChange?: (sort: CarrierSortOption) => void;
}

const PropertyCarriersComparisonTable = ({
  availableCarriers,
  carrierQuotes,
  locations,
  insuranceHistory,
  selectedCarrierQuoteId,
  rfqStatus,
  onSelectCarrier,
  onEditCoverages,
  onOpenMarketOptions,
  onGetQuotes,
  isSelectingCarrier = false,
  isGettingQuotes = false,
  canGetQuotes = true,
  isCapturingRiskDetails = false,
  riskDetailsSync,
  sortBy,
  onSortChange,
}: PropertyCarriersComparisonTableProps) => {
  const [marketMessagesExpanded, setMarketMessagesExpanded] = useState(false);

  const { theme, isLoading, getQuoteForCarrier, isCarrierSelected } =
    useComparisonTableHelpers({
      carrierQuotes,
      selectedCarrierQuoteId,
      rfqStatus,
    });

  const { isItemExpanded, toggleItem, toggleSection, getExpandedSections } =
    useExpandableItems({ items: locations });

  const getLocationLabel = (
    location: CarriersLocation,
    index: number,
  ): string => {
    if (location.address) {
      const parts = [location.address, location.city, location.postal_code]
        .filter(Boolean)
        .join(', ');
      return parts || `Location ${index + 1}`;
    }
    return `Location ${index + 1}`;
  };

  const getLocationPremium = (
    quote: CarrierQuote,
    locationIndex: number,
  ): string | null => {
    return quote.location_premiums?.[locationIndex] ?? null;
  };

  return (
    <TableWrapper theme={theme}>
      <Thead>
        <ComparisonTableHeader
          availableCarriers={availableCarriers}
          carrierQuotes={carrierQuotes}
          selectedCarrierQuoteId={selectedCarrierQuoteId}
          onSelectCarrier={onSelectCarrier}
          onOpenMarketOptions={onOpenMarketOptions}
          onGetQuotes={onGetQuotes}
          isLoading={isLoading}
          isSelectingCarrier={isSelectingCarrier}
          isGettingQuotes={isGettingQuotes}
          canGetQuotes={canGetQuotes}
          isCapturingRiskDetails={isCapturingRiskDetails}
          riskDetailsSync={riskDetailsSync}
          theme={theme}
          sortBy={sortBy}
          onSortChange={onSortChange}
        />
      </Thead>
      <Tbody>
        {locations.map((location, locationIndex) => {
          const isLocationExpanded = isItemExpanded(location.id);

          return (
            <React.Fragment key={location.id}>
              <ItemHeaderRow
                label={getLocationLabel(location, locationIndex)}
                isExpanded={isLocationExpanded}
                onToggle={() => toggleItem(location.id)}
                onEdit={
                  onEditCoverages
                    ? () => onEditCoverages(location.id)
                    : undefined
                }
                isEditDisabled={isLoading}
                availableCarriers={availableCarriers}
                getQuoteForCarrier={getQuoteForCarrier}
                isCarrierSelected={isCarrierSelected}
                isLoading={isLoading}
                theme={theme}
                getPremiumForQuote={(quote) =>
                  getLocationPremium(quote, locationIndex)
                }
                premiumSubLabel="excl. taxes"
              />

              {isLocationExpanded && (
                <LocationCoveragesSection
                  location={location}
                  locationIndex={locationIndex}
                  availableCarriers={availableCarriers}
                  carrierQuotes={carrierQuotes}
                  selectedCarrierQuoteId={selectedCarrierQuoteId}
                  expandedSections={getExpandedSections(location.id)}
                  isLoading={isLoading}
                  theme={theme}
                  onToggleSection={(section) =>
                    toggleSection(location.id, section)
                  }
                  insuranceHistory={insuranceHistory}
                />
              )}

              {locationIndex < locations.length - 1 && !isLocationExpanded && (
                <ItemSeparatorRow
                  colSpan={availableCarriers.length + 2}
                  borderColor={theme.borderColor}
                />
              )}
            </React.Fragment>
          );
        })}

        <MarketMessagesSection
          availableCarriers={availableCarriers}
          carrierQuotes={carrierQuotes}
          selectedCarrierQuoteId={selectedCarrierQuoteId}
          isExpanded={marketMessagesExpanded}
          onToggle={() => setMarketMessagesExpanded(!marketMessagesExpanded)}
          theme={theme}
        />

        <PremiumRow
          label="Estimated Total Premium"
          getValue={(quote) =>
            (quote?.status === 'quoted' || quote?.status === 'pending') &&
            quote?.annual_premium != null
              ? formatCurrency(quote.annual_premium)
              : '-'
          }
          isBold
          availableCarriers={availableCarriers}
          getQuoteForCarrier={getQuoteForCarrier}
          isCarrierSelected={isCarrierSelected}
          isLoading={isLoading}
          theme={theme}
        />

        <MonthlyPremiumSection
          availableCarriers={availableCarriers}
          getQuoteForCarrier={getQuoteForCarrier}
          isCarrierSelected={isCarrierSelected}
          isLoading={isLoading}
          theme={theme}
        />
      </Tbody>
    </TableWrapper>
  );
};

export default PropertyCarriersComparisonTable;
